<template>
    <div class="md-layout">
        <div class="md-layout-item pw-reset-layout">
            <md-card class="pw-card">
                <md-card-content>
                    <!-- Verifying the reset link -->
                    <template v-if="status === 'verifying'">
                        <p class="pw-copy">Checking your reset link…</p>
                    </template>

                    <!-- Link invalid/expired/already used -->
                    <template v-else-if="status === 'invalid'">
                        <h2 class="pw-title">This link is no longer valid</h2>
                        <p class="pw-copy">{{ errorMessage }}</p>
                        <router-link to="/forgot-password" class="pw-back-link">
                            Request a new reset link
                        </router-link>
                    </template>

                    <!-- Valid code: show the new-password form -->
                    <template v-else-if="status === 'ready'">
                        <h2 class="pw-title">Set a new password</h2>
                        <p class="pw-copy" v-if="accountEmail">
                            For <strong>{{ accountEmail }}</strong>
                        </p>

                        <form @submit.prevent="handleSubmit">
                            <md-field :class="{ 'md-invalid': errorMessage }">
                                <label>New password</label>
                                <md-input type="password" v-model="password" required autocomplete="new-password"
                                    :disabled="submitting" />
                            </md-field>

                            <md-field :class="{ 'md-invalid': errorMessage }">
                                <label>Confirm new password</label>
                                <md-input type="password" v-model="confirmPassword" required autocomplete="new-password"
                                    :disabled="submitting" />
                                <span class="md-error" v-if="errorMessage">{{ errorMessage }}</span>
                            </md-field>

                            <md-button type="submit" class="pw-btn pw-btn-primary"
                                :disabled="submitting || !password || !confirmPassword">
                                {{ submitting ? "Updating…" : "Update password" }}
                            </md-button>
                        </form>
                    </template>

                    <!-- Success -->
                    <template v-else-if="status === 'done'">
                        <h2 class="pw-title">Password updated</h2>
                        <p class="pw-copy">You can now sign in with your new password.</p>
                        <router-link to="/login" class="pw-back-link">Go to sign in</router-link>
                    </template>
                </md-card-content>
            </md-card>
        </div>
    </div>
</template>

<script>
// Same assumptions as ForgotPassword.vue: `auth` exported from your firebase
// config module alongside `db`, using the v8 compat/namespaced SDK. See that
// file's header comment for details.
import { auth } from "@/config/firebaseInit.js";

const MIN_PASSWORD_LENGTH = 8;

export default {
    name: "ResetPassword",
    data() {
        return {
            status: "verifying", // 'verifying' | 'invalid' | 'ready' | 'done'
            oobCode: "",
            accountEmail: "",
            password: "",
            confirmPassword: "",
            submitting: false,
            errorMessage: "",
        };
    },
    async created() {
        // Firebase appends `mode` and `oobCode` (and sometimes `apiKey`,
        // `continueUrl`, `lang`) to the URL it puts in the reset email, per
        // the `actionCodeSettings.url` passed to sendPasswordResetEmail in
        // ForgotPassword.vue. This route must match that URL's path.
        const { mode, oobCode } = this.$route.query;

        if (mode !== "resetPassword" || !oobCode) {
            this.status = "invalid";
            this.errorMessage = "This reset link is malformed or incomplete.";
            return;
        }

        this.oobCode = oobCode;

        try {
            // Verify the code BEFORE showing the password form, so someone
            // with an expired/already-used link finds out immediately
            // instead of typing a new password first and only then failing.
            this.accountEmail = await auth.verifyPasswordResetCode(oobCode);
            this.status = "ready";
        } catch (error) {
            this.status = "invalid";
            this.errorMessage = this.messageForError(error);
        }
    },
    methods: {
        async handleSubmit() {
            if (this.submitting) return;
            this.errorMessage = "";

            if (this.password !== this.confirmPassword) {
                this.errorMessage = "Passwords don't match.";
                return;
            }
            if (this.password.length < MIN_PASSWORD_LENGTH) {
                this.errorMessage = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
                return;
            }

            this.submitting = true;
            try {
                await auth.confirmPasswordReset(this.oobCode, this.password);
                this.status = "done";
            } catch (error) {
                // A code can expire/get consumed between verify and submit
                // (e.g. the link was opened in two tabs) -- handle that here
                // too, not just at initial verification.
                if (
                    error.code === "auth/expired-action-code" ||
                    error.code === "auth/invalid-action-code"
                ) {
                    this.status = "invalid";
                    this.errorMessage = this.messageForError(error);
                } else if (error.code === "auth/weak-password") {
                    this.errorMessage = "That password is too weak. Try a longer or more varied one.";
                } else {
                    console.error("confirmPasswordReset failed:", error);
                    this.errorMessage = "Something went wrong. Please try again.";
                }
            } finally {
                this.submitting = false;
            }
        },

        messageForError(error) {
            if (error.code === "auth/expired-action-code") {
                return "This link has expired. Reset links are only valid for a limited time.";
            }
            if (error.code === "auth/invalid-action-code") {
                return "This link has already been used or is invalid.";
            }
            if (error.code === "auth/user-disabled") {
                return "This account has been disabled.";
            }
            if (error.code === "auth/user-not-found") {
                return "We couldn't find the account for this link.";
            }
            return "This reset link is no longer valid.";
        },
    },
};
</script>

<style scoped>
.pw-reset-layout {
    max-width: 420px;
    margin: 64px auto;
}

.pw-card {
    padding: 8px 4px;
    border-radius: 14px;
    box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}

.pw-title {
    margin: 0 0 8px;
    font-size: 20px;
}

.pw-copy {
    color: #4b5563;
    font-size: 14px;
    margin-bottom: 20px;
}

.pw-btn {
    width: 100%;
    margin-top: 8px;
    border-radius: 999px;
    text-transform: none;
}

.pw-btn-primary {
    background: #253C6E !important;
    color: #f9fafb !important;
}

.pw-btn-secondary {
    background: #e5e7eb !important;
    color: #253C6E !important;
}

.pw-back-link {
    display: block;
    text-align: center;
    margin-top: 16px;
    font-size: 13px;
    color: #253C6E;
}
</style>